import { useLanguage } from '@/contexts/LanguageContext';
import heroImg1 from '@/assets/hero-salzburg.jpg?format=webp&quality=80';
import heroImg2 from '@/assets/hero-fleet.png?format=webp&quality=80';
import heroImg3 from '@/assets/hero-taxi-night.jpg?format=webp&quality=80';
import { Phone, MessageCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect } from 'react';
import HomeBookingWidget from './booking/HomeBookingWidget';

const heroImages = [heroImg1, heroImg2, heroImg3];

const HeroSection = () => {
  const { t } = useLanguage();
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative min-h-[90vh] flex items-center overflow-hidden">
      <div className="absolute inset-0">
        <AnimatePresence>
          <motion.img
            key={currentIndex}
            src={heroImages[currentIndex]}
            alt={['Salzburg Altstadt panoramic view – Taxi Salzburg premium transfer service', 'Taxi Salzburg premium fleet – Mercedes E-Class and V-Class vehicles', 'Night taxi transfer in Salzburg – 24/7 airport and city taxi service'][currentIndex]}
            className="absolute inset-0 w-full h-full object-cover"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 2, ease: "easeInOut" }}
          />
        </AnimatePresence>
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, hsla(0,0%,0%,0.35), hsla(0,0%,0%,0.5))' }} />
      </div>

      <div className="relative z-10 container py-16 md:py-20">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          {/* Left: Booking widget */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
            className="order-2 lg:order-1"
          >
            <HomeBookingWidget />
          </motion.div>

          {/* Right: Hero copy */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="order-1 lg:order-2 text-center lg:text-left"
          >
            <span className="inline-block px-4 py-1.5 rounded-full bg-primary/20 text-primary text-sm font-medium mb-5 backdrop-blur-sm border border-primary/30">
              ⭐ Taxi Salzburg — {t.features.service247}
            </span>
            <h1 className="font-display text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-5 leading-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]">
              {t.hero.title}
            </h1>
            <p className="text-base md:text-lg text-white/90 mb-6 drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)] max-w-xl mx-auto lg:mx-0">
              {t.hero.subtitle}
            </p>
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3">
              <a href="tel:+436606521071" dir="ltr" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white font-semibold hover:bg-white/20 transition">
                <Phone className="w-4 h-4" /> +43 660 6521071
              </a>
              <a href="https://wa.me/436606521071" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-green-600 text-white font-semibold hover:bg-green-700 transition">
                <MessageCircle className="w-4 h-4" /> {t.hero.whatsapp}
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
